import { useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE } from "@/lib/api";
import { useCart } from "./cart";
import { sp } from "@/store/nav";
import { BadgeCheck, Camera, Heart, Recycle, ShieldCheck } from "lucide-react";
import { useWishlist } from "./wishlist";

export const photoUrl = (id, w) => (id ? `${API_BASE}/store/photos/${id}${w ? `?w=${w}` : ""}` : null);
export const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

export const COND = {
  "used-like-new": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "used-good": "bg-sky-100 text-sky-800 border-sky-200",
  "used-fair": "bg-amber-100 text-amber-800 border-amber-200",
  "for-parts": "bg-rose-100 text-rose-800 border-rose-200",
};

export function ConditionBadge({ condition, label, className = "" }) {
  const { lang } = useCart();
  const text = label?.[lang] || label?.en || condition?.replace("used-", "").replace("-", " ");
  return <span data-testid="condition-badge" className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${COND[condition] || "bg-slate-100 text-slate-700 border-slate-200"} ${className}`}>{text}</span>;
}

export function Photo({ id, alt = "", className = "", contain = true, w = 400, priority = false, width, height }) {
  const [broken, setBroken] = useState(false);
  const url = photoUrl(id, w);
  if (!url || broken) return <div className={`flex items-center justify-center bg-slate-100 text-slate-300 ${className}`}><Camera className="h-8 w-8" /></div>;
  return <img src={url} alt={alt} width={width} height={height} loading={priority ? "eager" : "lazy"} fetchPriority={priority ? "high" : undefined} onError={() => setBroken(true)} className={`${contain ? "object-contain" : "object-cover"} bg-white ${className}`} />;
}

export function Availability({ n, reserved = 0, size = "text-xs" }) {
  const { st } = useCart();
  if (!n) return <span className={`rounded-full bg-slate-100 px-2 py-0.5 ${size} font-medium text-slate-500`} data-testid="availability-chip">{st("sold_out")}</span>;
  const low = n < 3;
  return <span data-testid="availability-chip" className={`rounded-full px-2 py-0.5 ${size} font-medium ${low ? "bg-orange-100 text-orange-700" : "bg-emerald-50 text-emerald-800"}`}>{n === 1 ? st("one_available") : `${n} ${st("available")}`}{reserved ? ` · ${reserved} ${st("reserved")}` : ""}</span>;
}

export function PartNumber({ value, className = "" }) {
  return <span className={`font-mono text-xs tracking-wide text-emerald-800 bg-emerald-50 rounded px-1.5 py-0.5 ${className}`} data-testid="part-number">{value || "—"}</span>;
}

const COND_LABEL = {
  "used-like-new": ["Como nueva", "Like new"], "used-good": ["Buen estado", "Good condition"], "good": ["Buen estado", "Good condition"], "used": ["Usada", "Used"],
  "used-fair": ["Usada · Aceptable", "Used · Fair"], "for-parts": ["Para repuestos", "For parts"],
};
const COND_TONE = { "used-like-new": "bg-emerald-700 text-white", "used-good": "bg-emerald-700 text-white", good: "bg-emerald-700 text-white", "used-fair": "bg-amber-400 text-slate-900", used: "bg-amber-400 text-slate-900", "for-parts": "bg-rose-600 text-white" };

export function ProductCard({ g }) {
  const { st, lang } = useCart();
  const { has, toggle } = useWishlist();
  const best = g.conditions?.[0];
  const condLabel = best ? (COND_LABEL[best]?.[lang === "es" ? 0 : 1] || best.replace(/used-/g, "").replace(/-/g, " ")) : "";
  const fits = (g.compatibility || []).slice(0, 2).join(", ");
  const saved = has(g.product_id);
  return (
    <article className="group relative flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-[box-shadow,transform,border-color] duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-xl hover:shadow-slate-200/80 md:rounded-2xl">
      <Link to={sp(`/p/${g.slug}`)} data-testid={`product-card-${g.part_number}`} className="flex flex-1 flex-col">
        <div className="relative aspect-square w-full overflow-hidden bg-slate-50 p-1 md:p-2">
          <Photo id={g.photo} alt={g.title} w={800} className="h-full w-full rounded-lg transition-transform duration-500 group-hover:scale-105" />
          {condLabel && <span data-testid="condition-badge" className={`absolute left-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-bold md:left-3 md:top-3 md:px-2.5 md:py-1 md:text-[11px] ${COND_TONE[best] || "bg-slate-700 text-white"}`}>{condLabel}</span>}
        </div>
        <div className="flex flex-1 flex-col gap-1 p-2.5 md:p-4">
          {g.match_type && <span data-testid={`match-${g.product_id}`} className="text-xs text-slate-600">{g.match_type === "exact" ? (lang === "es" ? "Coincidencia exacta" : "Exact match") : (lang === "es" ? "Relacionado · compatibilidad no garantizada" : "Related · compatibility not guaranteed")}</span>}
          <h3 className="line-clamp-2 font-heading text-sm font-semibold leading-snug text-slate-900 md:text-base">{g.title}</h3>
          <div className="hidden text-sm text-slate-600 md:block">{lang === "es" ? "N.º de parte" : "Part #"}: <span className="font-mono text-slate-800" data-testid="part-number">{g.part_number || "—"}</span></div>
          {(fits || g.brand) && <div className="hidden truncate text-sm text-slate-500 md:block">{lang === "es" ? "Compatible" : "Fits"}: {fits || g.brand}</div>}
          <div className="mt-auto flex flex-wrap items-end justify-between gap-1 pt-2 md:flex-nowrap md:gap-2 md:pt-3">
            <div className="font-heading text-base font-bold text-slate-900 md:text-xl" data-testid={`price-from-${g.product_id}`}>{g.manual_group && g.price_from !== g.price_to && <span className="mr-1 text-xs font-medium uppercase text-slate-400">{st("from")}</span>}{money(g.price_from)}</div>
            {g.manual_group ? <Availability n={g.available} size="text-[10px] md:text-xs" /> : !g.available && <span data-testid="availability-chip" className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 md:text-xs">{st("unit_reserved")}</span>}
          </div>
        </div>
      </Link>
      <button
        aria-label={saved ? (lang === "es" ? "Quitar de guardados" : "Remove from saved") : (lang === "es" ? "Guardar artículo" : "Save item")}
        className={`absolute right-2 top-2 flex h-8 w-8 items-center md:right-3 md:top-3 md:h-9 md:w-9 justify-center rounded-full border shadow-sm transition-colors ${saved ? "border-rose-200 bg-rose-50 text-rose-500" : "border-slate-200 bg-white text-slate-500 hover:border-rose-200 hover:text-rose-500"}`}
        data-testid={`save-product-${g.product_id}`}
        onClick={() => toggle(g)}
        type="button"
      >
        <Heart className="h-4 w-4" fill={saved ? "currentColor" : "none"} />
      </button>
    </article>
  );
}

export function ProductGrid({ items }) {
  return <div className="grid grid-cols-2 gap-3 md:grid-cols-[repeat(auto-fill,minmax(min(100%,17rem),1fr))] md:gap-5" data-testid="product-grid">{items.map((g) => <ProductCard key={g.product_id} g={g} />)}</div>;
}

const TRUST = {
  warranty: { Icon: ShieldCheck, es: ["Garantía funcional de 60 días", "desde la entrega, en todas las piezas usadas"], en: ["60-Day Functional Warranty", "from delivery, on every used part"] },
  tested: { Icon: BadgeCheck, es: ["Piezas identificadas", "cada unidad identificada por su código SKU/SL"], en: ["Identified Parts", "every unit identified by its SKU/SL code"] },
  reuse: { Icon: Recycle, es: ["Reutilizar es reparar mejor", "menos desperdicio, misma función"], en: ["Reuse is the smarter repair", "less waste, same function"] },
};

export function TrustBanner({ items, testId }) {
  const { lang } = useCart();
  return (
    <aside className="sl-trust-banner" data-testid={testId}>
      {items.map((k) => { const { Icon, [lang]: [title, sub] } = TRUST[k]; return <span key={k}><Icon className="h-4 w-4" /><span><strong>{title}</strong> · {sub}</span></span>; })}
      <Link to={sp("/policies#returns")} data-testid={`${testId}-link`}>{lang === "es" ? "Ver política" : "View policy"} →</Link>
    </aside>
  );
}
